import React, {createContext, useContext, useEffect, useState} from "react";
import {
    getTractorAccessoriesList,
    getTractorAttachmentsList,
    getTractorEquipmentList,
    getTractorModelList,
    getTractorTiresList,
    getTractorUpgradesList,
} from "../services/TractorService";
import {getCompanyData} from "../services/CommonService";
import {matchPath, useLocation, useNavigate} from "react-router-dom";

const ConfiguratorContext = createContext();

export const ConfiguratorProvider = ({children}) => {
    const [mainCompanyData, setMainCompanyData] = useState({});
    const [tractorLoader, setTractorLoader] = useState(true);
    const [tractorModelList, setTractorModelList] = useState([]);
    const [selectedTractor, setSelectedTractor] = useState(null);
    const [selectedTires, setSelectedTires] = useState([]);
    const [selectedUpgrades, setSelectedUpgrades] = useState([]);
    const [selectedAttachments, setSelectedAttachments] = useState([]);
    const [selectedEquipment, setSelectedEquipment] = useState([]);
    const [selectedAccessories, setSelectedAccessories] = useState([]);
    const [userNotes, setUserNotes] = useState("");
    const [tractorTiresList, setTractorTiresList] = useState([]);
    const [tractorUpgradeList, setTractorUpgradeList] = useState([]);
    const [tractorAttachmentList, setTractorAttachmentList] = useState([]);
    const [tractorEquimentList, setTractorEquimentList] = useState([]);
    const [tractorAccessoriesList, setTractorAccessoriesList] = useState([]);
    const [selectedModalTractor, setSelectedModalTractor] = useState(null);
    const [lastConfirmedTractorId, setLastConfirmedTractorId] = useState(null); // <-- NEW
    const [lastTractorId, setLastTractorId] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();
    const match = matchPath("/dealer/:dealerUuid/*", location.pathname);
    const dealerUuid = match?.params?.dealerUuid;

    const resetConfigurator = () => {
        setSelectedTractor(null);
        setSelectedTires(null);
        setSelectedUpgrades([]);
        setSelectedAttachments([]);
        setSelectedEquipment([]);
        setSelectedAccessories([]);
        setUserNotes("");
    };

    useEffect(() => {
        const data = {
            dealerUuid: dealerUuid
        };
        getCompanyData(data)
            .then(response => {
                setMainCompanyData(response.data.data);
            })
            .catch(err => {
                navigate("/maintenance");
            });
    }, [dealerUuid, navigate]);

    useEffect(() => {
        if (tractorModelList?.length === 0) {
            const data = {
                dealerUuid: dealerUuid
            };
            getTractorModelList(data)
                .then((response) => {
                    setTractorModelList(response.data.data);
                    setTractorLoader(false);
                })
                .catch((err) => {
                    setTractorLoader(false);
                });
        }
    }, [tractorModelList, dealerUuid]);

    useEffect(() => {
        if (lastTractorId) {
            const data = {
                uuid: selectedTractor?.uuid,
                dealerUuid: dealerUuid
            };

            getTractorTiresList(data)
                .then((response) => {
                    const tiresData = response.data.data;
                    setTractorTiresList(tiresData);

                    const hasTires = tiresData.some(
                        (group) => group.list && group?.list?.length > 0
                    );
                    if (!hasTires) {
                        setSelectedTires([]);
                    }
                })
                .catch((err) => {
                    setTractorTiresList([]);
                    setSelectedTires([]);
                });
        }
    }, [lastTractorId, selectedTractor, setSelectedTires, setTractorTiresList, dealerUuid]);

    useEffect(() => {
        if (lastTractorId) {
            let data = {
                uuid: selectedTractor?.uuid,
                dealerUuid: dealerUuid
            };
            getTractorUpgradesList(data)
                .then((response) => {
                    const upgradeData = response.data.data;
                    setTractorUpgradeList(upgradeData);
                    const hasTires = upgradeData.length > 0;
                    if (!hasTires) {
                        setSelectedUpgrades([]);
                    }
                })
                .catch((err) => {
                    console.log("err", err);
                    setTractorUpgradeList([]);
                    setSelectedUpgrades([]);
                });
        }
    }, [lastTractorId, selectedTractor, setSelectedUpgrades, dealerUuid]);

    useEffect(() => {
        if (lastTractorId) {
            let data = {
                uuid: selectedTractor?.uuid,
                dealerUuid: dealerUuid
            };
            getTractorAttachmentsList(data)
                .then((response) => {
                    let attData = response?.data?.data;
                    setTractorAttachmentList(attData);

                    const hasTires = attData?.length > 0;

                    if (!hasTires) {
                        setSelectedAttachments([]);
                    }
                })
                .catch((err) => {
                    setTractorAttachmentList([]);
                    setSelectedAttachments([]);
                });
        }
    }, [
        lastTractorId,
        selectedTractor,
        setSelectedAttachments,
        setTractorAttachmentList,
        dealerUuid
    ]);

    useEffect(() => {
        if (lastTractorId) {
            let data = {
                uuid: selectedTractor?.uuid,
                dealerUuid: dealerUuid
            };
            getTractorAccessoriesList(data)
                .then((response) => {
                    let accData = response.data.data;
                    setTractorAccessoriesList(accData);
                    const hasTires = accData.length > 0;
                    if (!hasTires) {
                        setSelectedAccessories([]);
                    }
                })
                .catch((err) => {
                    console.log("err", err);
                    setSelectedAccessories([]);
                    setTractorAccessoriesList([]);
                });
        }
    }, [
        lastTractorId,
        selectedTractor,
        setSelectedAccessories,
        setTractorAccessoriesList,
        dealerUuid
    ]);

    useEffect(() => {
        if (lastTractorId) {
            let data = {
                uuid: selectedTractor?.uuid,
                dealerUuid: dealerUuid
            };
            getTractorEquipmentList(data)
                .then((response) => {
                    const equiData = response.data.data;
                    setTractorEquimentList(equiData);
                    const hasTires = equiData.length > 0;
                    if (!hasTires) {
                        setSelectedEquipment([]);
                    }
                })
                .catch((err) => {
                    console.log("err", err);
                    setTractorEquimentList([]);
                    setSelectedEquipment([]);
                });
        }
    }, [
        lastTractorId,
        selectedTractor,
        setSelectedEquipment,
        setTractorEquimentList,
        dealerUuid
    ]);

    return (
        <ConfiguratorContext.Provider
            value={{
                mainCompanyData,
                tractorLoader,
                setTractorLoader,
                tractorModelList,
                selectedTractor,
                setSelectedTractor,
                selectedTires,
                setSelectedTires,
                selectedUpgrades,
                setSelectedUpgrades,
                selectedAttachments,
                setSelectedAttachments,
                selectedEquipment,
                setSelectedEquipment,
                selectedAccessories,
                setSelectedAccessories,
                userNotes,
                setUserNotes,
                resetConfigurator,
                tractorTiresList,
                tractorUpgradeList,
                tractorAttachmentList,
                tractorEquimentList,
                tractorAccessoriesList,
                setSelectedModalTractor,
                selectedModalTractor,
                lastConfirmedTractorId,
                setLastConfirmedTractorId,
                setLastTractorId,
                lastTractorId,
            }}
        >
            {children}
        </ConfiguratorContext.Provider>
    );
};

export const useConfigurator = () => useContext(ConfiguratorContext);
